import { COACH_TIER_BADGE_STYLES } from './coachUiConstants';

/**
 * Maps CRS list/detail payloads (HPS_CRS_PLAYER_CONTRACT.md) to player UI fields.
 */

function inferTierFromSkillLevels(levels) {
  const s = (levels || []).join(' ').toLowerCase();
  if (s.includes('elite') || s.includes('pro')) return 'elite';
  if (s.includes('advanced') || s.includes('performance')) return 'performance';
  return 'coach';
}

/** Prefer CRS `coach_tier` when present; map to badge keys used in `COACH_TIER_BADGE_STYLES`. */
function resolveCoachTierKey(crs) {
  const raw = crs.coach_tier;
  if (raw != null && String(raw).trim() !== '') {
    const k = String(raw).trim().toLowerCase();
    if (k === 'elite' || k === 'performance' || k === 'coach') return k;
    if (k.includes('elite') || k === 'pro') return 'elite';
    if (k.includes('performance')) return 'performance';
    if (k.includes('advanced')) return 'performance';
  }
  return inferTierFromSkillLevels(crs.skill_levels);
}

function skillLevelsToFilterKeys(levels) {
  const keys = [];
  const s = (levels || []).map((x) => String(x).toLowerCase()).join(' ');
  if (s.includes('beginner')) keys.push('beginner');
  if (s.includes('intermediate') || s.includes('confirmed')) keys.push('intermediate');
  if (s.includes('advanced')) keys.push('advanced');
  if (s.includes('elite')) keys.push('elite');
  return keys.length ? keys : ['intermediate'];
}

/** CRS may send explicit flags; otherwise infer from profile_status. */
function resolveIsAvailableForBooking(crs) {
  if (typeof crs.is_available === 'boolean') return crs.is_available;
  if (typeof crs.accepting_bookings === 'boolean') return crs.accepting_bookings;
  if (typeof crs.open_for_booking === 'boolean') return crs.open_for_booking;
  const ps = String(crs.profile_status || '').toLowerCase();
  if (ps === 'inactive' || ps === 'suspended' || ps === 'unavailable' || ps === 'paused') {
    return false;
  }
  return ps === 'active' || ps === 'available' || ps === '';
}

function availabilityLabelFromCrs(crs, isAvail) {
  if (isAvail) return 'Available';
  const ps = String(crs.profile_status || '').trim();
  if (!ps) return 'Unavailable';
  return ps.charAt(0).toUpperCase() + ps.slice(1).replace(/_/g, ' ');
}

/** @param {Record<string, any>} crs - item from GET /crs/coaches */
export function normalizeCoachListItem(crs) {
  const tierKey = resolveCoachTierKey(crs);
  const tierStyle = COACH_TIER_BADGE_STYLES[tierKey] || COACH_TIER_BADGE_STYLES.coach;
  const clubs = Number(crs.affiliated_clubs_count) || 0;
  const priceCents =
    typeof crs.price_from_cents === 'number' && Number.isFinite(crs.price_from_cents)
      ? crs.price_from_cents
      : null;
  const priceChf = priceCents != null ? priceCents / 100 : null;
  const isAvailableBooking = resolveIsAvailableForBooking(crs);
  return {
    id: crs.id,
    display_name: crs.full_name || 'Coach',
    about_me: crs.bio || '',
    tagline:
      crs.available_packages_summary ||
      crs.pricing_notes ||
      (clubs ? `${clubs} partner club${clubs === 1 ? '' : 's'}` : 'Padel coaching'),
    profile_photo_url: crs.photo_url || null,
    banner_image_url: crs.banner_image_url || null,
    // Explicit `false` skips BFF fetch; omitted key keeps legacy “try /photo” behaviour.
    has_photo_image: crs.has_photo_image === false ? false : true,
    has_banner_image: crs.has_banner_image === true,
    languages: Array.isArray(crs.languages) ? crs.languages : [],
    specialties: Array.isArray(crs.certifications) ? crs.certifications : [],
    skill_levels_served: skillLevelsToFilterKeys(crs.skill_levels),
    session_types: ['private'],
    coach_tier: tierKey,
    coach_tier_label: `${tierStyle.label} Coach`,
    rating_average: typeof crs.rating_average === 'number' ? crs.rating_average : null,
    review_count: typeof crs.review_count === 'number' ? crs.review_count : 0,
    price_from_cents: priceCents,
    price_from_amount: priceChf,
    currency: 'CHF',
    primary_club_name: crs.primary_club_name || '',
    city: crs.city || '',
    available_this_week: isAvailableBooking,
    is_available_booking: isAvailableBooking,
    availability_summary: isAvailableBooking ? 'available' : 'unavailable',
    availability_label: availabilityLabelFromCrs(crs, isAvailableBooking),
    profile_status: crs.profile_status,
    next_session_starts_at: null,
    affiliated_clubs_count: clubs,
    _raw: crs,
  };
}

/** @param {Record<string, any>} crs - from GET /crs/coaches/:id */
export function normalizeCoachDetail(crs) {
  const base = normalizeCoachListItem(crs);
  const clubList = Array.isArray(crs.clubs) ? crs.clubs : [];
  const firstClub = clubList[0];
  return {
    ...base,
    about_me: crs.bio || base.about_me,
    clubs: clubList,
    primary_club_name: firstClub?.club_name || base.primary_club_name,
    profile_status: crs.profile_status,
  };
}

/** @param {Record<string, any>} o - offering from GET .../offerings */
export function normalizeOffering(o) {
  const minP = o.min_players ?? 1;
  const maxP = o.max_players ?? minP;
  let subtitle = `${minP} player${minP !== 1 ? 's' : ''}`;
  if (maxP !== minP) subtitle = `${minP}–${maxP} players`;
  return {
    id: o.id,
    label: o.title || o.session_type || 'Session',
    subtitle,
    duration_minutes: o.duration_minutes || 60,
    coach_fee_cents: o.coach_fee_cents ?? 0,
    coach_fee_chf: (o.coach_fee_cents ?? 0) / 100,
    club_id: o.club_id,
    club_name: o.club_name || '',
    session_type: o.session_type || '',
    skill_level: o.skill_level || '',
    difficulty_copy: o.skill_level ? String(o.skill_level) : 'All levels',
    _raw: o,
  };
}

/** Group slots by local calendar date key YYYY-MM-DD (browser local). */
export function groupSlotsByDate(slots) {
  const map = {};
  for (const slot of slots || []) {
    const d = new Date(slot.starts_at);
    const key = [
      d.getFullYear(),
      String(d.getMonth() + 1).padStart(2, '0'),
      String(d.getDate()).padStart(2, '0'),
    ].join('-');
    if (!map[key]) map[key] = [];
    map[key].push(slot);
  }
  const dates = Object.keys(map).sort();
  return { slotsByDate: map, dates };
}

export function formatSlotTimeLabel(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}
